import { config, getRedirectUri, getTenantBaseUrl } from "./config";
import { t } from "./i18n";
import { AuthSession, DialogOAuthMessage, KrebitUser, Language, OAuthTokenResponse } from "./types";

const tenantStorageKey = "krebitSign.tenant";
const pendingAuthStorageKey = "krebitSign.pendingAuth";
const sessionStoragePrefix = "krebitSign.auth.";
const workspaceDomainSuffix = ".krebit.se";

export class WorkspaceNotFoundError extends Error {
  public constructor() {
    super(t("workspaceNotFoundTitle"));
    this.name = "WorkspaceNotFoundError";
  }
}

interface PendingAuth {
  tenant: string;
  state: string;
  codeVerifier: string;
  createdAt: number;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";

  for (let index = 0; index < bytes.byteLength; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function randomBase64Url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function createCodeChallenge(codeVerifier: string): Promise<string> {
  const encoded = new TextEncoder().encode(codeVerifier);
  const digest = await crypto.subtle.digest("SHA-256", encoded);
  return base64UrlEncode(new Uint8Array(digest));
}

function formEncode(data: Record<string, string>): string {
  return Object.keys(data)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join("&");
}

function getAuthStorageKey(tenant: string): string {
  return `${sessionStoragePrefix}${tenant}`;
}

function expiresAtFromToken(token: OAuthTokenResponse): number {
  return Date.now() + token.expires_in * 1000;
}

export function isValidTenant(value: string): boolean {
  return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(value);
}

export function normalizeTenant(value: string): string {
  let normalized = value.trim().toLowerCase();

  if (!normalized) {
    return "";
  }

  if (/^[a-z][a-z0-9+.-]*:\/\//.test(normalized)) {
    try {
      const url = new URL(normalized);
      const hasOnlyRootPath = url.pathname === "" || url.pathname === "/";

      if (
        (url.protocol === "https:" || url.protocol === "http:") &&
        hasOnlyRootPath &&
        !url.search &&
        !url.hash &&
        !url.username &&
        !url.password &&
        !url.port
      ) {
        normalized = url.hostname;
      }
    } catch (_error) {
      return normalized;
    }
  } else {
    normalized = normalized.replace(/\/+$/, "");
  }

  if (normalized.endsWith(workspaceDomainSuffix)) {
    return normalized.slice(0, -workspaceDomainSuffix.length);
  }

  return normalized;
}

export function loadTenant(): string {
  return localStorage.getItem(tenantStorageKey) || "";
}

export function saveTenant(tenant: string): void {
  localStorage.setItem(tenantStorageKey, tenant);
}

export function loadSession(tenant: string): AuthSession | null {
  const raw = localStorage.getItem(getAuthStorageKey(tenant));

  if (!raw) {
    return null;
  }

  try {
    const session = JSON.parse(raw) as AuthSession;
    return session.tenant === tenant ? session : null;
  } catch (_error) {
    localStorage.removeItem(getAuthStorageKey(tenant));
    return null;
  }
}

export function saveSession(session: AuthSession): void {
  localStorage.setItem(getAuthStorageKey(session.tenant), JSON.stringify(session));
}

export function clearSession(tenant: string): void {
  localStorage.removeItem(getAuthStorageKey(tenant));
  localStorage.removeItem(pendingAuthStorageKey);
}

export function hasUsableClientId(): boolean {
  return Boolean(config.oauthClientId && config.oauthClientId !== "replace-with-krebit-oauth-client-id");
}

export async function verifyTenantExists(tenant: string): Promise<void> {
  if (!isValidTenant(tenant)) {
    throw new WorkspaceNotFoundError();
  }

  let response: Response;

  try {
    response = await fetch(`${getTenantBaseUrl(tenant)}/api/oauth/token`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: formEncode({
        grant_type: "workspace_check",
        client_id: config.oauthClientId || "office-addin",
      }),
    });
  } catch (_error) {
    throw new WorkspaceNotFoundError();
  }

  const contentType = response.headers.get("Content-Type") || "";

  if (response.status === 404 || !contentType.toLowerCase().includes("application/json")) {
    throw new WorkspaceNotFoundError();
  }
}

export async function createAuthStartUrl(tenant: string, language: Language): Promise<string> {
  const codeVerifier = randomBase64Url(64);
  const codeChallenge = await createCodeChallenge(codeVerifier);
  const state = randomBase64Url(32);
  const pendingAuth: PendingAuth = {
    tenant,
    state,
    codeVerifier,
    createdAt: Date.now(),
  };

  localStorage.setItem(pendingAuthStorageKey, JSON.stringify(pendingAuth));

  const query = formEncode({
    tenant,
    client_id: config.oauthClientId,
    redirect_uri: getRedirectUri(),
    state,
    code_challenge: codeChallenge,
    locale: language.toLowerCase(),
  });

  return `${config.appBaseUrl}/auth/start.html?${query}`;
}

export async function exchangeAuthorizationCode(message: DialogOAuthMessage): Promise<AuthSession> {
  const pendingAuth = getPendingAuth(message.state);

  if (!message.code) {
    throw new Error(t("cannotCompleteSignIn"));
  }

  const token = await requestToken(pendingAuth.tenant, {
    grant_type: "authorization_code",
    client_id: config.oauthClientId,
    redirect_uri: getRedirectUri(),
    code: message.code,
    code_verifier: pendingAuth.codeVerifier,
  });

  localStorage.removeItem(pendingAuthStorageKey);

  const session: AuthSession = {
    tenant: pendingAuth.tenant,
    accessToken: token.access_token,
    refreshToken: token.refresh_token,
    expiresAt: expiresAtFromToken(token),
  };

  saveSession(session);
  return session;
}

export async function refreshSession(session: AuthSession): Promise<AuthSession> {
  if (!session.refreshToken) {
    throw new Error(t("signInExpired"));
  }

  const token = await requestToken(session.tenant, {
    grant_type: "refresh_token",
    client_id: config.oauthClientId,
    refresh_token: session.refreshToken,
  });

  const refreshedSession: AuthSession = {
    ...session,
    accessToken: token.access_token,
    refreshToken: token.refresh_token || session.refreshToken,
    expiresAt: expiresAtFromToken(token),
  };

  saveSession(refreshedSession);
  return refreshedSession;
}

export async function getValidSession(session: AuthSession): Promise<AuthSession> {
  const refreshMarginMs = 60 * 1000;

  if (Date.now() < session.expiresAt - refreshMarginMs) {
    return session;
  }

  return refreshSession(session);
}

export function withUser(session: AuthSession, user: KrebitUser): AuthSession {
  const nextSession = { ...session, user };
  saveSession(nextSession);
  return nextSession;
}

function getPendingAuth(state?: string): PendingAuth {
  const raw = localStorage.getItem(pendingAuthStorageKey);

  if (!raw) {
    throw new Error(t("signInAttemptMissing"));
  }

  const pendingAuth = JSON.parse(raw) as PendingAuth;

  if (!state || state !== pendingAuth.state) {
    throw new Error(t("signInAttemptUnverified"));
  }

  if (Date.now() - pendingAuth.createdAt > 10 * 60 * 1000) {
    localStorage.removeItem(pendingAuthStorageKey);
    throw new Error(t("signInAttemptExpired"));
  }

  return pendingAuth;
}

async function requestToken(tenant: string, data: Record<string, string>): Promise<OAuthTokenResponse> {
  const response = await fetch(`${getTenantBaseUrl(tenant)}/api/oauth/token`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: formEncode(data),
  });

  const responseBody = await readResponseBody(response);

  if (!response.ok) {
    throw new Error(getApiErrorMessage(responseBody, t("cannotCompleteSignIn")));
  }

  return responseBody as OAuthTokenResponse;
}

async function readResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch (_error) {
    return { message: text };
  }
}

function getApiErrorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === "object") {
    const value = body as { message?: string; error?: string; error_description?: string };
    return value.message || value.error_description || value.error || fallback;
  }

  return fallback;
}
